import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../../context/stateContext";
import {
  FileText,
  Calendar,
  Building2,
  Filter,
  Search,
  ChevronDown,
} from "lucide-react";

const RetailItemHistory = () => {
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();

  // State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeValue, setDateRangeValue] = useState("Last 30 Days");
  const [showDateRanges, setShowDateRanges] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingDrugs, setLoadingDrugs] = useState(false);

  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");

  // Drug Search State
  const [drugs, setDrugs] = useState([]);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDrugDropdown, setShowDrugDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Refs
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Drugs on Debounce
  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      getDrugs(debouncedSearch);
    } else {
      setDrugs([]);
    }
  }, [debouncedSearch]);

  // Auto-focus input when dropdown opens
  useEffect(() => {
    if (showDrugDropdown && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showDrugDropdown]);

  // Handle Outside Click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDrugDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const selectedElement = listRef.current.children[highlightedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [highlightedIndex]);

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?paginated=false`,
        "GET"
      );
      setBranches(data);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const getDrugs = async (search_query) => {
    setLoadingDrugs(true);
    setHighlightedIndex(-1);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${activeSession?.branch?.id}?page=1&pageSize=100&search=${search_query}`,
        "GET"
      );
      setDrugs(data?.results || []);
    } catch (error) {
      console.error("Error fetching drugs:", error);
      setDrugs([]);
    } finally {
      setLoadingDrugs(false);
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRangeValue(value);
    let start_date = "";
    let end_date = "";

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (value === "Custom") {
      setShowDateRanges(true);
      return;
    }

    setShowDateRanges(false);

    if (value === "Today") {
      start_date = formatDate(new Date());
      end_date = formatDate(new Date());
    } else if (value === "Yesterday") {
      start_date = formatDate(new Date(new Date().setDate(new Date().getDate() - 1)));
      end_date = formatDate(new Date(new Date().setDate(new Date().getDate() - 1)));
    } else if (value === "Last 7 Days") {
      start_date = formatDate(new Date(new Date().setDate(new Date().getDate() - 7)));
      end_date = formatDate(new Date());
    } else if (value === "Last 30 Days") {
      start_date = formatDate(new Date(new Date().setDate(new Date().getDate() - 30)));
      end_date = formatDate(new Date());
    } else if (value === "This Month") {
      start_date = formatDate(new Date(new Date().setDate(1)));
      end_date = formatDate(new Date());
    } else if (value === "Last Month") {
      start_date = formatDate(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1));
      end_date = formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
    } else if (value === "This Year") {
      start_date = formatDate(new Date(new Date().getFullYear(), 0, 1));
      end_date = formatDate(new Date());
    } else if (value === "Last Year") {
      start_date = formatDate(new Date(new Date().getFullYear() - 1, 0, 1));
      end_date = formatDate(new Date(new Date().getFullYear() - 1, 11, 31));
    }

    setStartDate(start_date);
    setEndDate(end_date);
  };

  const handleGenerateReport = async () => {
    if (branch === "") {
      return toast.error("Please select a branch");
    }
    window.location.href = `/report/inventory/history/retail/print?branch=${branch}&start_date=${startDate}&end_date=${endDate}&drug=${selectedDrug?.drug?.id}`;
  };

  const handleKeyDown = (e) => {
    if (!showDrugDropdown) {
      if (e.key === "Enter" || e.key === " ArrowDown") {
        setShowDrugDropdown(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < drugs.length) {
        handleSelectDrug(drugs[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowDrugDropdown(false);
    }
  };

  const handleSelectDrug = (drugItem) => {
    setSelectedDrug(drugItem);
    setSearch("");
    setShowDrugDropdown(false);
    setHighlightedIndex(-1);
  };

  useEffect(() => {
    fetchBranches();
    handleDateRangeChange("Last 30 Days");
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <span>Reports</span>
            <span>/</span>
            <span>Inventory</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">History</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText size={32} className="text-blue-600" />
            Inventory History Report
          </h1>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Filter className="text-blue-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Report Criteria
                </h2>
                <p className="text-sm text-gray-500">
                  Select parameters to generate the report
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Drug Selection with Enhanced Search */}
            <div className="relative" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search size={16} className="inline mr-2" />
                Drug Name
              </label>

              <div
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-white cursor-pointer flex items-center justify-between hover:border-blue-500 transition-colors"
                onClick={() => setShowDrugDropdown(!showDrugDropdown)}
              >
                <span className={selectedDrug ? "text-gray-900" : "text-gray-400"}>
                  {selectedDrug?.drug?.name || "Search and select a drug..."}
                </span>
                <ChevronDown size={16} className="text-gray-400" />
              </div>

              {showDrugDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type at least 2 characters..."
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="max-h-60 overflow-y-auto" ref={listRef}>
                    {loadingDrugs ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <div className="animate-spin inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
                        Searching...
                      </div>
                    ) : drugs.length > 0 ? (
                      drugs.map((item, index) => (
                        <div
                          key={index}
                          onClick={() => handleSelectDrug(item)}
                          className={`px-4 py-2 text-sm cursor-pointer transition-colors ${index === highlightedIndex
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          <div className="font-medium">{item?.drug?.name}</div>
                          <div className="text-xs text-gray-500">
                            GHS {item?.drug?.selling_price}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        {search.length < 2
                          ? "Type to search..."
                          : "No drugs found"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Branch Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 size={16} className="inline mr-2" />
                Branch
              </label>
              <select
                onChange={(e) => setBranch(e.target.value)}
                value={branch}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">Select Branch</option>
                {branches.map((branchItem, index) => (
                  <React.Fragment key={index}>
                    {!branchItem?.is_head_office && !branchItem?.is_stores && (
                      <option value={branchItem.id}>
                        {branchItem.name}
                      </option>
                    )}
                  </React.Fragment>
                ))}
              </select>
            </div>

            {/* Date Range Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-2" />
                Date Range
              </label>
              <select
                onChange={(e) => handleDateRangeChange(e.target.value)}
                value={dateRangeValue}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="Last 7 Days">Last 7 Days</option>
                <option value="Last 30 Days">Last 30 Days</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
                <option value="This Year">This Year</option>
                <option value="Last Year">Last Year</option>
                <option value="Custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Inputs */}
            {showDateRanges && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4 border-t border-gray-100">
              <button
                onClick={handleGenerateReport}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <RotatingLines
                      visible={true}
                      height="20"
                      width="20"
                      strokeColor="white"
                      strokeWidth="5"
                      animationDuration="0.75"
                    />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <FileText size={20} />
                    <span>Generate Report</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailItemHistory;
